import React, { useState } from 'react';
import { GripVertical, Trash2, Heart, Star, QrCode, ImagePlus } from 'lucide-react';
import type { StampPresetItem } from '../../services/api';

// 內建 icon 映射
const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  Heart, Star, QrCode, ImagePlus,
};

/** 棋盤格背景樣式（表示透明區域） */
const checkerboardStyle: React.CSSProperties = {
  backgroundImage: [
    'linear-gradient(45deg, #e2e8f0 25%, transparent 25%)',
    'linear-gradient(-45deg, #e2e8f0 25%, transparent 25%)',
    'linear-gradient(45deg, transparent 75%, #e2e8f0 75%)',
    'linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)',
  ].join(', '),
  backgroundSize: '12px 12px',
  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
};

interface StampCardProps {
  preset: StampPresetItem;
  onToggle: (id: string, enabled: boolean) => void;
  onDelete: (id: string) => void;
  /** 拖曳相關 */
  isDragging?: boolean;
  dragHandleProps?: Record<string, any>;
}

const StampCard: React.FC<StampCardProps> = ({
  preset, onToggle, onDelete, isDragging, dragHandleProps,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const IconComponent = preset.icon ? ICON_MAP[preset.icon] : null;

  return (
    <div className={`relative group bg-white dark:bg-slate-900 border rounded-2xl p-3 flex flex-col items-center gap-2 transition-all w-32 ${
      isDragging
        ? 'border-blue-400 shadow-lg shadow-blue-500/20 scale-105'
        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
    }`}>
      {/* 拖曳手把 */}
      <div
        {...dragHandleProps}
        className="absolute top-1.5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-slate-300 dark:text-slate-600"
      >
        <GripVertical size={14} />
      </div>

      {/* 圖片預覽區 */}
      <div
        className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0"
        style={preset.type === 'custom' ? checkerboardStyle : undefined}
      >
        {IconComponent ? (
          <IconComponent size={28} className="text-slate-500 dark:text-slate-400" />
        ) : preset.imageUrl ? (
          <img src={preset.imageThumbnailUrl || preset.imageUrl} alt={preset.name} className="w-full h-full object-contain" />
        ) : preset.builtinDataUrl ? (
          <img src={preset.builtinDataUrl} alt={preset.name} className="w-10 h-10 object-contain" />
        ) : (
          <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 text-xs">
            無圖
          </div>
        )}
      </div>

      {/* 名稱 */}
      <span className="text-xs font-medium text-slate-700 dark:text-slate-300 text-center truncate w-full">
        {preset.name}
      </span>

      {/* 啟用/停用 Toggle */}
      <button
        onClick={() => onToggle(preset.id, !preset.enabled)}
        className={`w-full flex items-center justify-center gap-1.5 py-1 rounded-lg text-[10px] font-medium transition-all ${
          preset.enabled
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500'
        }`}
      >
        <div className={`w-6 h-3.5 rounded-full relative transition-colors ${
          preset.enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
        }`}>
          <div className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white shadow-sm transition-all ${
            preset.enabled ? 'left-3' : 'left-0.5'
          }`} />
        </div>
        {preset.enabled ? '啟用' : '停用'}
      </button>

      {/* 類型標籤 + 刪除鈕 */}
      <div className="flex items-center justify-between w-full">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${
          preset.type === 'builtin'
            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-400'
            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
        }`}>
          {preset.type === 'builtin' ? '內建' : '自訂'}
        </span>

        {preset.type === 'custom' && (
          confirmDelete ? (
            <div className="flex gap-1">
              <button
                onClick={() => { onDelete(preset.id); setConfirmDelete(false); }}
                className="text-[10px] text-red-500 hover:text-red-700 font-bold"
              >
                確認
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-[10px] text-slate-400 hover:text-slate-600"
              >
                取消
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400"
            >
              <Trash2 size={12} />
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default StampCard;
